import { Paper, Typography, Box, IconButton, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useState } from "react";
import { tokens } from "../../../theme";

interface MiniCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
}

export default function MiniCalendarWidget({
  selectedDate,
  onDateSelect,
}: MiniCalendarProps) {

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString("default", { month: "long" });

  const jsFirstDay = new Date(year, month, 1).getDay();
  const firstDay = (jsFirstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const dates: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const days = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <Paper
      elevation={0}
      sx={{
        containerType: "inline-size",
        borderRadius: "16px",
        p: 1.5,
        height: "100%",
        backgroundColor: colors.primary[900],
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.1,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Typography
          variant="subtitle2"
          fontWeight={700}
          color={colors.primary[400]}
          sx={{ fontSize: "clamp(0.55rem, 4cqw, 0.75rem)" }}
        >
          {monthName.toUpperCase()}
        </Typography>

        <IconButton
          size="small"
          onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Day labels */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.3,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        {days.map((day, i) => (
          <Typography
            key={i}
            variant="caption"
            color={colors.primary[300]}
            fontWeight={600}
            sx={{ fontSize: "clamp(0.6rem, 3cqw, 0.7rem)" }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.3,
          textAlign: "center",
          flex: 1,
          minHeight: 0,
        }}
      >
        {dates.map((date, index) => {
          const isToday =
            date === today.getDate() &&
            month === today.getMonth() &&
            year === today.getFullYear();
          const isSelected =
            date === selectedDate.getDate() &&
            month === selectedDate.getMonth() &&
            year === selectedDate.getFullYear();

          return (
            <Box
              key={index}
              onClick={() => date && onDateSelect(new Date(year, month, date))}
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "5px",
                cursor: date ? "pointer" : "default",
                backgroundColor: isSelected
                  ? colors.primary[300]
                  : isToday
                    ? colors.primary[800]
                    : "transparent",
                color: isSelected ? colors.primary[900] : isToday ? colors.primary[400] : colors.primary[300],
                fontWeight: isSelected || isToday ? 700 : 500,
                fontSize: "clamp(0.6rem, 3cqw, 0.7rem)",
                "&:hover": date
                  ? { backgroundColor: isSelected ? colors.primary[400] : colors.primary[800] }
                  : {},
              }}
            >
              {date ?? ""}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}